import { moveToFulfilled, objectWithoutKeys, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { DeleteMessageFulfilledAction } from '../../domains/deleteMessage/actions.types';
import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type {
  DismissModeratedThreadFulfilledAction,
  PublishModeratedThreadFulfilledAction,
  RestoreModeratedThreadFulfilledAction,
} from '../../domains/messageWithModerationState/actions.types';
import type { UpdateModerationStateAction } from '../../domains/messageWithModerationState/updateModerationStateAction.types';
import type {
  DismissModeratedEventAgentReplyFulfilledAction,
  PublishModeratedEventAgentReplyFulfilledAction,
  ReconcileModeratedEventAgentReplyAction,
  RestoreModeratedEventAgentReplyFulfilledAction,
} from '../../domains/moderatedEventAgentReplies/actions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type { PublishTeamsMeetingMessageFulfilledAction } from '../../domains/publishers/actions.types';
import type {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/actions.types';
import type { ThreadSharesLoadFulfilledAction } from '../../domains/threadShares/actions.types';
import type {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';
import { ThreadModerationStateChange } from '../threads/types';

import type { MessageModerationStates } from './types';
export type MessageModerationStateAction =
  | ReconcileModeratedEventAgentReplyAction
  | DeleteMessageFulfilledAction
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | PublishModeratedThreadFulfilledAction
  | DismissModeratedThreadFulfilledAction
  | RestoreModeratedThreadFulfilledAction
  | DismissModeratedEventAgentReplyFulfilledAction
  | PublishModeratedEventAgentReplyFulfilledAction
  | RestoreModeratedEventAgentReplyFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | ThreadSharesLoadFulfilledAction
  | PublishTeamsMeetingMessageFulfilledAction
  | UpdateModerationStateAction;
const initialState: MessageModerationStates = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};
const updateModerationState = (
  state: MessageModerationStates,
  threadId: string,
  moderationState: ThreadModerationStateChange
) => {
  if (moderationState === ThreadModerationStateChange.DISMISSED) {
    return update(state, threadId, { moderationState: 'DISMISSED' });
  }

  if (moderationState === ThreadModerationStateChange.PENDING) {
    return update(state, threadId, { moderationState: 'PENDING' });
  }

  return update(state, threadId, { moderationState: undefined });
};
const reducer: Reducer<MessageModerationStates, MessageModerationStateAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'MODERATED_EVENT_AGENT_REPLY.RECONCILED':
      return moveToFulfilled(state, {
        [action.payload.messageId]: {
          id: action.payload.messageId,
          moderationState: action.payload.moderationState,
        },
      });
    case 'MESSAGE.DELETE_FULFILLED':
      return action.payload.isModeratedEventAgentReply
        ? {
            ...state,
            fulfilled: {
              ...state.fulfilled,
              byId: objectWithoutKeys(state.fulfilled.byId, [action.payload.messageId]),
            },
          }
        : state;
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'PUBLISH_MODERATED_THREAD.FULFILLED':
      return update(state, action.payload.threadStarterId, { moderationState: undefined });
    case 'DISMISS_MODERATED_THREAD.FULFILLED':
      return update(state, action.payload.threadStarterId, { moderationState: 'DISMISSED' });
    case 'RESTORE_MODERATED_THREAD.FULFILLED':
      return update(state, action.payload.threadStarterId, { moderationState: 'PENDING' });
    case 'DISMISS_MODERATED_EVENT_AGENT_REPLY.FULFILLED':
      return update(state, action.payload.messageId, { moderationState: 'DISMISSED' });
    case 'PUBLISH_MODERATED_EVENT_AGENT_REPLY.FULFILLED':
      return update(state, action.payload.messageId, { moderationState: undefined });
    case 'RESTORE_MODERATED_EVENT_AGENT_REPLY.FULFILLED':
      return update(state, action.payload.messageId, { moderationState: 'PENDING' });
    case 'THREAD_SHARES.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messageModerationStates);
    case 'THREAD.UPDATE_MODERATION_STATE':
      return updateModerationState(state, action.payload.threadStarterId, action.payload.moderationState);
    default:
      return state;
  }
};
export default reducer;
